import React, { useState, useEffect, useCallback } from 'react';
import { UsuarioListItem, DatosDocente, DatosDocentePayload, TipoContrato, GradoAcademico, Sexo } from 'types/usuario.types';
import { usuarioService } from 'services/usuarioService';
import { datosDocenteService } from 'services/datosDocenteService';
import AsignacionFormPage from './AsignacionFormPage';
import UsuarioFormPage from './UsuarioFormPage';

// ============================================================
// DocentesPage – Lista de docentes + asignación + datos profesionales
// ============================================================

type Vista = 'list' | 'asignacion' | 'new';

const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_SEARCH  = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_ASSIGN  = 'M10 2a.75.75 0 0 1 .75.75v.258a33.186 33.186 0 0 1 6.668.83.75.75 0 0 1-.336 1.461 31.28 31.28 0 0 0-1.103-.232l1.702 7.545a.75.75 0 0 1-.387.832A4.981 4.981 0 0 1 15 14c-.825 0-1.606-.2-2.294-.556a.75.75 0 0 1-.387-.832l1.77-7.849a31.743 31.743 0 0 0-3.339-.254v11.505a20.01 20.01 0 0 1 3.78.501.75.75 0 1 1-.339 1.462A18.51 18.51 0 0 0 10 17.5c-1.442 0-2.845.165-4.191.477a.75.75 0 0 1-.338-1.462 20.01 20.01 0 0 1 3.779-.501V4.509a31.743 31.743 0 0 0-3.339.254l1.77 7.849a.75.75 0 0 1-.387.832A4.98 4.98 0 0 1 5 14a4.98 4.98 0 0 1-2.294-.556.75.75 0 0 1-.387-.832l1.702-7.545c-.37.07-.738.147-1.103.232a.75.75 0 0 1-.336-1.46 33.186 33.186 0 0 1 6.668-.83V2.75A.75.75 0 0 1 10 2Z';
const D_PREV    = 'M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z';
const D_NEXT    = 'M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 1 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z';
const D_PROF    = 'M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-7 9a7 7 0 1 1 14 0H3Z';
const D_CLOSE   = 'M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z';
const D_SAVE    = 'M10 .5a9.5 9.5 0 1 1 0 19 9.5 9.5 0 0 1 0-19ZM8.22 9.22a.75.75 0 0 0 0 1.06l2.5 2.5a.75.75 0 0 0 1.06 0l4.5-4.5a.75.75 0 0 0-1.06-1.06L10.97 11.19 9.28 9.5a.75.75 0 0 0-1.06-.28Z';

const PAGE_SIZE = 12;

const Spin = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

// ── Panel de datos profesionales ─────────────────────────────

const SEXO_OPTS: { value: Sexo; label: string }[] = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
];
const GRADO_OPTS: { value: GradoAcademico; label: string }[] = [
  { value: 'BACHILLER',  label: 'Bachiller' },
  { value: 'LICENCIADO', label: 'Licenciado' },
  { value: 'MAGISTER',   label: 'Magíster' },
  { value: 'DOCTOR',     label: 'Doctor' },
  { value: 'TECNICO',    label: 'Técnico' },
];
const CONTRATO_OPTS: { value: TipoContrato; label: string }[] = [
  { value: 'NOMBRADO',   label: 'Nombrado' },
  { value: 'CONTRATADO', label: 'Contratado' },
  { value: 'CAS',        label: 'CAS' },
  { value: 'PRACTICANTE',label: 'Practicante' },
];

const EMPTY_FORM: DatosDocentePayload = {
  sexo: null, gradoAcademico: null, carrera: null,
  numeroColegiatura: null, tipoContrato: null, anioIngreso: null,
};

interface ProfPanelProps {
  docente: UsuarioListItem;
  onClose: () => void;
  onStatusChange: (userId: number, hasData: boolean) => void;
}

const ProfPanel: React.FC<ProfPanelProps> = ({ docente, onClose, onStatusChange }) => {
  const [form, setForm]         = useState<DatosDocentePayload>(EMPTY_FORM);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState(false);

  useEffect(() => {
    setLoading(true); setError(null); setSuccess(false);
    datosDocenteService.get(docente.idUsuario).then(data => {
      onStatusChange(docente.idUsuario, !!data);
      if (data) {
        setForm({
          sexo: data.sexo,
          gradoAcademico: data.gradoAcademico,
          carrera: data.carrera,
          numeroColegiatura: data.numeroColegiatura,
          tipoContrato: data.tipoContrato,
          anioIngreso: data.anioIngreso,
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }).catch(e => setError(e instanceof Error ? e.message : 'Error al cargar datos'))
      .finally(() => setLoading(false));
  }, [docente.idUsuario]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field: keyof DatosDocentePayload, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value === '' ? null : field === 'anioIngreso' ? Number(value) : value,
    }));
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError(null); setSuccess(false);
    try {
      await datosDocenteService.save(docente.idUsuario, form);
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al guardar datos');
    } finally { setSaving(false); }
  };

  const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );

  const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400";
  const selectCls = inputCls;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end" onClick={onClose}>
      <div
        className="relative h-full w-full max-w-md bg-white shadow-2xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div className="flex-shrink-0 bg-gradient-to-br from-indigo-600 to-indigo-800 px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-white font-bold text-base leading-tight">
                Datos Profesionales
              </h2>
              <p className="text-indigo-200 text-xs mt-0.5">
                {docente.apellidos}, {docente.nombres}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-indigo-200 hover:text-white hover:bg-indigo-700 transition-colors">
              <Ico d={D_CLOSE} cls="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
              <Spin />
              <span className="text-sm">Cargando…</span>
            </div>
          ) : (
            <form id="prof-form" onSubmit={handleSubmit} className="space-y-4">

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-medium">
                  Datos guardados correctamente.
                </div>
              )}

              <Field label="Sexo">
                <select
                  value={form.sexo ?? ''}
                  onChange={e => handleChange('sexo', e.target.value)}
                  className={selectCls}>
                  <option value="">— Seleccionar —</option>
                  {SEXO_OPTS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Grado Académico">
                <select
                  value={form.gradoAcademico ?? ''}
                  onChange={e => handleChange('gradoAcademico', e.target.value)}
                  className={selectCls}>
                  <option value="">— Seleccionar —</option>
                  {GRADO_OPTS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Carrera / Especialidad">
                <input
                  type="text"
                  value={form.carrera ?? ''}
                  onChange={e => handleChange('carrera', e.target.value)}
                  placeholder="Ej. Educación Matemática"
                  className={inputCls}
                />
              </Field>

              <Field label="N° Colegiatura">
                <input
                  type="text"
                  value={form.numeroColegiatura ?? ''}
                  onChange={e => handleChange('numeroColegiatura', e.target.value)}
                  placeholder="Ej. 12345678"
                  className={inputCls}
                />
              </Field>

              <Field label="Tipo de Contrato">
                <select
                  value={form.tipoContrato ?? ''}
                  onChange={e => handleChange('tipoContrato', e.target.value)}
                  className={selectCls}>
                  <option value="">— Seleccionar —</option>
                  {CONTRATO_OPTS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Año de Ingreso">
                <input
                  type="number"
                  min={1990}
                  max={new Date().getFullYear()}
                  value={form.anioIngreso ?? ''}
                  onChange={e => handleChange('anioIngreso', e.target.value)}
                  placeholder="Ej. 2018"
                  className={inputCls}
                />
              </Field>

            </form>
          )}
        </div>

        {/* Pie */}
        {!loading && (
          <div className="flex-shrink-0 border-t border-slate-100 px-5 py-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors">
              Cancelar
            </button>
            <button
              type="submit"
              form="prof-form"
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700
                         text-white text-sm font-semibold transition-colors disabled:opacity-50">
              {saving ? <Spin /> : <Ico d={D_SAVE} cls="w-4 h-4" />}
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Componente principal ─────────────────────────────────────

const DocentesPage: React.FC = () => {
  const [vista, setVista]                   = useState<Vista>('list');
  const [selectedDocente, setSelected]      = useState<UsuarioListItem | null>(null);
  const [profDocente, setProfDocente]        = useState<UsuarioListItem | null>(null);
  // docenteId → true(tiene datos prof) | false(sin datos) — se pobla al abrir el panel
  const [profStatus, setProfStatus]         = useState<Record<number, boolean>>({});

  const [docentes, setDocentes]             = useState<UsuarioListItem[]>([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState<string | null>(null);

  const [busqueda, setBusqueda]             = useState('');
  const [inputVal, setInputVal]             = useState('');
  const [page, setPage]                     = useState(0);
  const [totalPages, setTotalPages]         = useState(0);
  const [totalElements, setTotalElements]   = useState(0);

  const load = useCallback(async (p: number, b: string) => {
    setLoading(true); setError(null);
    try {
      const res = await usuarioService.listar({ rol: 'DOCENTE', page: p, size: PAGE_SIZE, busqueda: b || undefined });
      setDocentes(res.content);
      setTotalPages(res.page.totalPages);
      setTotalElements(res.page.totalElements);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar docentes');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(page, busqueda); }, [page, busqueda, load]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    setBusqueda(inputVal.trim());
  };

  const handleAsignar = (docente: UsuarioListItem) => {
    setSelected(docente);
    setVista('asignacion');
  };

  const handleBack = () => {
    setSelected(null);
    setVista('list');
    load(page, busqueda);
  };

  const handleProfStatus = useCallback((userId: number, hasData: boolean) => {
    setProfStatus(prev => ({ ...prev, [userId]: hasData }));
  }, []);

  // ── Vista nuevo docente ───────────────────────────────────────
  if (vista === 'new') {
    return (
      <UsuarioFormPage
        rolFijo="DOCENTE"
        onSuccess={() => { setVista('list'); load(0, ''); }}
        onCancel={() => setVista('list')}
      />
    );
  }

  // ── Vista asignación ──────────────────────────────────────────
  if (vista === 'asignacion' && selectedDocente) {
    return (
      <AsignacionFormPage
        docente={selectedDocente}
        onSuccess={handleBack}
        onCancel={handleBack}
      />
    );
  }

  // ── Vista lista ───────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* Panel datos profesionales */}
      {profDocente && (
        <ProfPanel
          docente={profDocente}
          onClose={() => setProfDocente(null)}
          onStatusChange={handleProfStatus}
        />
      )}

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Docentes</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {totalElements} docente{totalElements !== 1 ? 's' : ''} registrado{totalElements !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setVista('new')}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white
                     font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"/>
          </svg>
          Nuevo Docente
        </button>
      </div>

      {/* Buscador */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Ico d={D_SEARCH} cls="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              placeholder="Buscar por nombre, apellido o DNI…"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50
                         text-sm text-slate-700 outline-none focus:ring-2 focus:ring-blue-500
                         placeholder:text-slate-400"
            />
          </div>
          <button type="submit"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                       text-white text-sm font-semibold transition-colors shadow-sm">
            <Ico d={D_SEARCH} />
            Buscar
          </button>
        </form>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
            <Spin />
            <span className="text-sm">Cargando docentes…</span>
          </div>
        ) : docentes.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">
            {busqueda ? `Sin resultados para "${busqueda}"` : 'No hay docentes registrados'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['#', 'Docente', 'DNI', 'Correo', 'Estado', 'Acciones'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-400
                                           uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {docentes.map((d, i) => (
                  <tr key={d.idUsuario} className="hover:bg-slate-50 transition-colors">

                    {/* # */}
                    <td className="px-4 py-3 text-xs text-slate-400 tabular-nums">
                      {page * PAGE_SIZE + i + 1}
                    </td>

                    {/* Docente */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center
                                        bg-blue-100 text-blue-700 font-bold text-sm">
                          {d.nombres.charAt(0)}{d.apellidos.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 leading-tight whitespace-nowrap">
                            {d.apellidos}, {d.nombres}
                          </p>
                          <p className="text-xs text-slate-400">@{d.username}</p>
                        </div>
                      </div>
                    </td>

                    {/* DNI */}
                    <td className="px-4 py-3 font-mono text-slate-600 whitespace-nowrap">{d.dni}</td>

                    {/* Email */}
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-[180px] truncate">
                      {d.email || <span className="text-slate-300">—</span>}
                    </td>

                    {/* Estado */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full
                        ${d.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${d.activo ? 'bg-green-500' : 'bg-slate-400'}`} />
                        {d.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>

                    {/* Acciones */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleAsignar(d)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                     bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-100
                                     transition-colors whitespace-nowrap">
                          <Ico d={D_ASSIGN} cls="w-3.5 h-3.5" />
                          Asignar cursos
                        </button>
                        <button
                          onClick={() => setProfDocente(d)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                     bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-100
                                     transition-colors whitespace-nowrap">
                          <Ico d={D_PROF} cls="w-3.5 h-3.5" />
                          Datos prof.
                          {profStatus[d.idUsuario] === true && (
                            <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" title="Datos completos" />
                          )}
                          {profStatus[d.idUsuario] === false && (
                            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" title="Sin datos" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-3">
            <span className="text-xs text-slate-400">
              Página {page + 1} de {totalPages} · {totalElements} docentes
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(p - 1, 0))}
                disabled={page === 0}
                className="p-1.5 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100
                           disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <Ico d={D_PREV} />
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx)
                .filter(idx => Math.abs(idx - page) <= 2)
                .map(idx => (
                  <button key={idx} onClick={() => setPage(idx)}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors
                      ${idx === page
                        ? 'bg-blue-700 text-white'
                        : 'text-slate-500 hover:bg-slate-100'}`}>
                    {idx + 1}
                  </button>
                ))}
              <button
                onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))}
                disabled={page >= totalPages - 1}
                className="p-1.5 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100
                           disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <Ico d={D_NEXT} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocentesPage;
